import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Save, ChevronDown, Star, Trash2 } from 'lucide-react';
import { useFilters, useCreateFilter, useDeleteFilter, useToggleFilterFavorite } from '../../hooks/useIssues';
import type { SavedFilter } from '../../services/issueService';
import toast from 'react-hot-toast';

interface TqlFilterProps {
  tql: string;
  onTqlChange: (tql: string) => void;
  onExecute: () => void;
  projectId: string;
  inline?: boolean;  // 한 줄 레이아웃
}

const TQL_KEYWORDS = [
  'status', 'type', 'priority', 'assignee', 'reporter', 'label',
  'created', 'updated', 'dueDate', 'text', 'storyPoints',
  'AND', 'OR', 'ORDER BY', 'ASC', 'DESC',
  'IN', 'NOT IN', 'HAS', 'CONTAINS', 'IS EMPTY', 'IS NOT EMPTY',
  'BETWEEN', 'OVERDUE', 'currentUser()',
  'BUG', 'TASK', 'STORY', 'EPIC', 'SUB_TASK',
  'TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CLOSED', 'REOPENED',
  'BLOCKER', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW',
];

export default function TqlFilter({ tql, onTqlChange, onExecute, projectId, inline }: TqlFilterProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredKeywords, setFilteredKeywords] = useState<string[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSavedFilters, setShowSavedFilters] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveScope, setSaveScope] = useState<'PERSONAL' | 'SHARED'>('PERSONAL');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const savedFiltersRef = useRef<HTMLDivElement>(null);

  const { data: filters } = useFilters({ projectId });
  const createFilter = useCreateFilter();
  const deleteFilter = useDeleteFilter();
  const toggleFavorite = useToggleFilterFavorite();

  // Autocomplete logic
  const handleInputChange = (value: string) => {
    onTqlChange(value);
    const lastWord = value.split(/[\s(,]+/).pop()?.toUpperCase() || '';
    if (lastWord.length >= 1) {
      const matches = TQL_KEYWORDS.filter((k) =>
        k.toUpperCase().startsWith(lastWord),
      );
      setFilteredKeywords(matches.slice(0, 8));
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const applySuggestion = (keyword: string) => {
    const parts = tql.split(/[\s(,]+/);
    parts.pop();
    const prefix = tql.substring(0, tql.length - (tql.split(/[\s(,]+/).pop()?.length || 0));
    onTqlChange(prefix + keyword + ' ');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onExecute();
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSave = async () => {
    if (!saveName.trim() || !tql.trim()) return;
    try {
      await createFilter.mutateAsync({
        name: saveName,
        query: tql,
        projectId,
        scope: saveScope,
      });
      toast.success('필터가 저장되었습니다.');
      setShowSaveDialog(false);
      setSaveName('');
    } catch {
      toast.error('필터 저장에 실패했습니다.');
    }
  };

  const handleLoadFilter = (filter: SavedFilter) => {
    onTqlChange(filter.query);
    setShowSavedFilters(false);
    onExecute();
  };

  // Click outside handler for saved filters dropdown
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (savedFiltersRef.current && !savedFiltersRef.current.contains(e.target as Node)) {
      setShowSavedFilters(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const btnSize = inline ? 'px-2.5 py-1.5 text-sm' : 'px-3 py-1.5 text-sm';
  const iconSize = inline ? 12 : 14;

  const inputEl = inline ? (
    <input
      ref={inputRef as any}
      type="text"
      value={tql}
      onChange={(e) => handleInputChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder='type = Bug AND priority IN (CRITICAL, HIGH)'
      className="w-full px-3 py-1.5 text-sm font-mono border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
    />
  ) : (
    <textarea
      ref={inputRef}
      value={tql}
      onChange={(e) => handleInputChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder='예: type = Bug AND priority IN (CRITICAL, HIGH) AND status != DONE'
      className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
      rows={2}
    />
  );

  const suggestions = showSuggestions && filteredKeywords.length > 0 && (
    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 max-h-40 overflow-y-auto">
      {filteredKeywords.map((kw) => (
        <button
          key={kw}
          className="block w-full text-left px-3 py-1 text-sm font-mono hover:bg-primary-50 text-gray-700"
          onClick={() => applySuggestion(kw)}
        >
          {kw}
        </button>
      ))}
    </div>
  );

  const actionButtons = (
    <>
      <button
        onClick={onExecute}
        className={`flex items-center gap-1 ${btnSize} font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors`}
      >
        <Play size={iconSize} />
        실행
      </button>
      <button
        onClick={() => {
          if (!tql.trim()) { toast.error('필터 쿼리를 먼저 입력해주세요.'); return; }
          setShowSaveDialog(true);
        }}
        className={`flex items-center gap-1 ${btnSize} font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors`}
      >
        <Save size={iconSize} />
        저장
      </button>
      <div className="relative" ref={savedFiltersRef}>
        <button
          onClick={() => setShowSavedFilters(!showSavedFilters)}
          className={`flex items-center gap-1 ${btnSize} font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors`}
        >
          필터
          <ChevronDown size={iconSize} />
        </button>
          {showSavedFilters && (
            <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 max-h-60 overflow-y-auto">
              {(!filters || filters.length === 0) && (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">
                  저장된 필터가 없습니다.
                </div>
              )}
              {filters?.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 group"
                >
                  <button
                    className="flex-1 text-left"
                    onClick={() => handleLoadFilter(f)}
                  >
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                      {f.isFavorite && <Star size={12} className="text-yellow-500 fill-yellow-500" />}
                      {f.name}
                    </div>
                    <div className="text-sm text-gray-500 font-mono truncate">{f.query}</div>
                  </button>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite.mutate(f.id);
                      }}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <Star
                        size={12}
                        className={f.isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}
                      />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteFilter.mutate(f.id);
                      }}
                      className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );

    // 저장 다이얼로그
    const saveDialog = showSaveDialog && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">필터 저장</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">필터 이름</label>
              <input type="text" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="예: 내 긴급 버그" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500" autoFocus />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">쿼리</label>
              <div className="px-3 py-2 text-sm font-mono bg-gray-50 border border-gray-200 rounded-lg text-gray-600">{tql}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">공개 범위</label>
              <select value={saveScope} onChange={(e) => setSaveScope(e.target.value as 'PERSONAL' | 'SHARED')} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                <option value="PERSONAL">개인 필터</option>
                <option value="SHARED">공유 필터</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setShowSaveDialog(false)} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
            <button onClick={handleSave} disabled={!saveName.trim() || createFilter.isPending} className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50">저장</button>
          </div>
        </div>
      </div>
    );

    // 렌더링
    if (inline) {
      return (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            {inputEl}
            {suggestions}
          </div>
          {actionButtons}
          {saveDialog}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="text-sm font-medium text-gray-500 uppercase tracking-wider">고급 필터 (TQL)</div>
        <div className="relative">{inputEl}{suggestions}</div>
        <div className="flex items-center gap-2">{actionButtons}</div>
        {saveDialog}
      </div>
    );
}
